import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
  TextField,
  Button,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Autocomplete,
} from '@mui/material';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useServicesTypesProject } from '../../../hooks/administration/useServicesTypesProject';
import { useRoutes } from '../../../hooks/useRoutes';
import { format, parseISO, isAfter, isEqual } from 'date-fns';

// Schema de validación extendido con Yup
const schema = yup.object().shape({
  serproid: yup.number().typeError('Tipo de servicio proyecto requerido').required('Tipo de servicio proyecto requerido'),
  rutid: yup.number().required('La Ruta es requerida'),
  ordsercabnom: yup.string().required('Nombre es obligatorio'),
  ordsercabfec: yup.date().typeError('Fecha requerida').required('Fecha de Inicio del Servicio requerida'),
  ordsercabfecfin: yup
    .date()
    .nullable()
    .test(
      'fecha-valida',
      'La fecha de fin debe ser igual o posterior a la de salida',
      function (value) {
        const { ordsercabfec } = this.parent;
        return value && ordsercabfec
          ? isAfter(value, ordsercabfec) || isEqual(value, ordsercabfec)
          : true;
      }
    ),
  ordsercabserprohij: yup.number().nullable(), // Campo opcional de Taxi enlazado
});

const ServiceOrderForm = ({ open, handleClose, onSubmit, defaultValues = {} }) => {
  const { servicesTypesProject } = useServicesTypesProject();
  const { routes } = useRoutes();

  const {
    register,
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      serproid: defaultValues.serproid ?? null,
      rutid: defaultValues.rutid ?? null,
      ordsercabnom: defaultValues.ordsercabnom || '',
      ordsercabfec: defaultValues.ordsercabfec || '',
      ordsercabfecfin: defaultValues.ordsercabfecfin || '',
      ordsercabserprohij: defaultValues.ordsercabserprohij ?? null, // <-- Inicializador
    },
  });

  useEffect(() => {
    if (defaultValues && open) {
      reset({
        serproid: defaultValues.serproid ?? null,
        rutid: defaultValues.rutid ?? null,
        ordsercabnom: defaultValues.ordsercabnom || '',
        ordsercabfec: defaultValues?.ordsercabfec
          ? format(parseISO(defaultValues.ordsercabfec), 'yyyy-MM-dd')
          : '',
        ordsercabfecfin: defaultValues?.ordsercabfecfin
          ? format(parseISO(defaultValues.ordsercabfecfin), 'yyyy-MM-dd')
          : '',
        ordsercabserprohij: defaultValues.ordsercabserprohij ?? null, // <-- Reset en edición
      });
    }
  }, [open, defaultValues, reset]);

  const formatDateOnly = (date) => {
    if (!date) return null;
    if (typeof date === 'string') return date;
    return `${date.getFullYear()}-${(date.getMonth() + 1)
      .toString()
      .padStart(2, '0')}-${date
        .getDate()
        .toString()
        .padStart(2, '0')}`;
  };

  // Observadores de estado de react-hook-form
  const selectedSerProId = watch('serproid');
  const startDateValue = watch('ordsercabfec');
  const endDateValue = watch('ordsercabfecfin');

  // Encontrar metadata del servicio principal seleccionado
  const selectedServiceType = (servicesTypesProject || []).find(
    (t) => t.serproid === selectedSerProId
  );
  const isInternalService = !!selectedServiceType?.serproint;
  const currentProjectID = selectedServiceType?.proid;

  // 💡 FILTRADO OPERATIVO DINÁMICO: 
  // Solo servicios con TipSerID = 3 (Taxi) que pertenezcan al mismo ProID de la OS Padre
  const taxiOptionsFiltered = (servicesTypesProject || []).filter(
    (s) => s.proid === currentProjectID && s.tipserid === 3 && s.estreg !== 'E'
  );

  // Limpiar el selector de taxi de forma reactiva si el usuario cambia a otra OS de otro proyecto
  useEffect(() => {
    setValue('ordsercabserprohij', null);
  }, [selectedSerProId, setValue]);

  // Cálculo de cantidad de órdenes para servicios internos
  let internalOrdersCount = null;
  if (isInternalService && startDateValue && endDateValue) {
    const start = new Date(startDateValue);
    const end = new Date(endDateValue);

    if (
      !isNaN(start.getTime()) &&
      !isNaN(end.getTime()) &&
      (isAfter(end, start) || isEqual(end, start))
    ) {
      const diffMs = end.getTime() - start.getTime();
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24)) + 1;
      internalOrdersCount = diffDays;
    } else {
      internalOrdersCount = 0;
    }
  }

  const handleFormSubmit = (data) => {
    onSubmit({
      ...data,
      ordsercabid: defaultValues?.ordsercabid ?? null,
      ordsercabfec: formatDateOnly(data.ordsercabfec),
      ordsercabfecfin: formatDateOnly(data.ordsercabfecfin),
      serproint: isInternalService,
      // Si es servicio interno, por diseño de flujo no hereda taxi directo en el formulario base
      ordsercabserprohij: isInternalService ? null : data.ordsercabserprohij,
    });
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle>
        {defaultValues?.ordsercabid
          ? 'Editar Orden de Servicio'
          : 'Nueva Orden de Servicio'}
      </DialogTitle>

      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <DialogContent>
          {/* Tipo de servicio */}
          <Controller
            name="serproid"
            control={control}
            render={({ field }) => {
              const selectedOption =
                (servicesTypesProject || []).find(
                  (opt) => opt.serproid === field.value
                ) || null;

              return (
                <Autocomplete
                  options={servicesTypesProject || []}
                  value={selectedOption}
                  onChange={(_, newValue) =>
                    field.onChange(newValue ? newValue.serproid : null)
                  }
                  onBlur={field.onBlur}
                  getOptionLabel={(option) => option?.serpronom ? `[${option.pronom}] ${option.serpronom}` : ''}
                  isOptionEqualToValue={(option, value) =>
                    option.serproid === value.serproid
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Tipo de Servicio Proyecto"
                      margin="normal"
                      fullWidth
                      error={!!errors.serproid}
                      helperText={errors.serproid?.message}
                    />
                  )}
                />
              );
            }}
          />

          {/* 🚖 NUEVO SELECTOR: Vinculación de Servicio de Taxi Derivado (Última milla) */}
          {!isInternalService && selectedSerProId && (
            <Controller
              name="ordsercabserprohij"
              control={control}
              render={({ field }) => {
                const selectedOption =
                (servicesTypesProject || []).find(
                  (opt) => opt.serproid === field.value
                ) || null;

                return (
                  <Autocomplete
                    options={taxiOptionsFiltered}
                    value={selectedOption}
                    onChange={(_, newValue) =>
                      field.onChange(newValue ? newValue.serproid : null)
                    }
                    onBlur={field.onBlur}
                    getOptionLabel={(option) => option?.serpronom || ''}
                    isOptionEqualToValue={(option, value) =>
                      option.serproid === value.serproid
                    }
                    noOptionsText="No hay servicios de TAXI configurados para este proyecto"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="¿Requiere Transbordo / Servicio de Taxi derivado? (Opcional)"
                        margin="normal"
                        fullWidth
                        placeholder="Seleccione el catálogo de taxi para la última milla"
                        error={!!errors.ordsercabserprohij}
                        helperText={
                          errors.ordsercabserprohij?.message ||
                          "Si se selecciona, habilitará el flujo de notificaciones y preparación de viajes de taxi en los manifiestos."
                        }
                      />
                    )}
                  />
                );
              }}
            />
          )}

          {/* Ruta */}
          <Controller
            name="rutid"
            control={control}
            render={({ field }) => {
              const selectedOption =
                (routes || []).find(
                  (opt) => String(opt.rutid) === String(field.value)
                ) || null;

              return (
                <Autocomplete
                  options={routes || []}
                  value={selectedOption}
                  onChange={(_, newValue) =>
                    field.onChange(newValue ? String(newValue.rutid) : '')
                  }
                  getOptionLabel={(option) => option?.rutnom || ''}
                  isOptionEqualToValue={(option, value) =>
                    String(option.rutid) === String(value.rutid)
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Ruta"
                      margin="normal"
                      fullWidth
                      error={!!errors.rutid}
                      helperText={errors.rutid?.message}
                    />
                  )}
                />
              );
            }}
          />

          {/* Nombre del servicio */}
          <TextField
            label="Nombre base del servicio"
            fullWidth
            margin="normal"
            {...register('ordsercabnom')}
            error={!!errors.ordsercabnom}
            helperText={
              errors.ordsercabnom?.message ||
              (isInternalService
                ? 'Ejemplo: Servicio interno. Se le añadirá la fecha automáticamente.'
                : '')
            }
          />

          {/* Mensaje especial para servicios internos */}
          {isInternalService && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Este tipo de servicio es <b>interno</b>. Se crearán órdenes diarias desde la
              fecha inicial hasta la fecha final, una por cada día. El nombre será:
              <br />
              <i>"Nombre base (dd-MM-yyyy)"</i>
            </Alert>
          )}

          {/* Fecha de Salida / Inicial */}
          <TextField
            label={isInternalService ? 'Fecha inicial (desde)' : 'Fecha de Salida'}
            type="date"
            fullWidth
            margin="normal"
            {...register('ordsercabfec')}
            error={!!errors.ordsercabfec}
            helperText={errors.ordsercabfec?.message}
            InputLabelProps={{ shrink: true }}
          />

          {/* Fecha de Fin */}
          <TextField
            label={
              isInternalService ? 'Fecha final (hasta)' : 'Fecha de Fin (Opcional)'
            }
            type="date"
            fullWidth
            margin="normal"
            {...register('ordsercabfecfin')}
            error={!!errors.ordsercabfecfin}
            helperText={
              errors.ordsercabfecfin?.message ||
              (isInternalService ? 'Obligatorio para generar varias órdenes.' : '')
            }
            InputLabelProps={{ shrink: true }}
          />

          {/* Resumen de cuántas órdenes se crearán */}
          {isInternalService && internalOrdersCount !== null && (
            <Alert
              severity={internalOrdersCount > 0 ? 'info' : 'warning'}
              sx={{ mt: 2 }}
            >
              {internalOrdersCount > 0
                ? `Se crearán ${internalOrdersCount} órdenes de servicio internas en este rango.`
                : 'Rango de fechas inválido: la fecha final debe ser igual o posterior a la inicial.'}
            </Alert>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose}>Cancelar</Button>
          <Button type="submit" variant="contained">
            {defaultValues?.ordsercabid ? 'Actualizar' : 'Guardar'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ServiceOrderForm;